import React, { ChangeEvent, useEffect, useState } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { TablePagination, Tooltip } from '@material-ui/core'
import { ReactComponent as DownloadIcon } from 'assets/img/download-icon.svg'
import CircleLoader from 'components/Loading/CircleLoader'
import { StyledIconWrap } from 'components/common/Inputs/styles'
import { Table } from 'styles/app/apps/main-style'
import { FormatMessage } from 'types/common'
import { Map, List } from 'immutable'
import { connect } from 'react-redux'
import Psfs from 'lib/psfs/Psfs'
import { PdfReport } from 'types/pdfReports'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { TABLET_BREAKPOINT } from 'helpers/responsive'
import messages from './translate'

type PdfReportsTableProps = WrappedComponentProps & {
  intl: { formatMessage: FormatMessage }
  fetchPdfReportData: (psfs: Psfs) => void
  loader: boolean
  tablePsfs: Psfs
  tableData: Map<string, { data: List<PdfReport>; count: number }>
}

type ActionsColumnProps = {
  id: number
  formatMessage: FormatMessage
  s3DownloadUri: string
}

const columns: string[] = ['title', 'dateCreated', 'actions']

function ActionsColumn({
  id,
  formatMessage,
  s3DownloadUri,
}: ActionsColumnProps) {
  const onDownload = (event: { stopPropagation: () => void }) => {
    event.stopPropagation()
    const link = document.createElement('a')
    link.download = 'pdf'
    link.href = s3DownloadUri

    if (ifWindowWidthLessThan(TABLET_BREAKPOINT)) {
      link.target = '_blank'
    }

    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  return (
    <td>
      <StyledIconWrap onClick={onDownload} key={id}>
        <Tooltip placement="top" title={formatMessage(messages.pdfDownload)}>
          <DownloadIcon />
        </Tooltip>
      </StyledIconWrap>
    </td>
  )
}

function PdfReportsTable({
  intl: { formatMessage },
  fetchPdfReportData,
  tableData,
  tablePsfs,
  loader,
}: PdfReportsTableProps) {
  const { data, count } = tableData.toJS() as {
    data: Array<PdfReport>
    count: number
  }
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)

  useEffect(() => {
    const { path } = tablePsfs
    const psfs = new Psfs(path)
    psfs.addPagination(page + 1, rowsPerPage)
    fetchPdfReportData(psfs)
  }, [page, rowsPerPage])

  return (
    <Table direction="column">
      <div className="table-wrapper">
        <table>
          <thead>
            <tr className="table-row">
              {columns.map(column => (
                <th key={column}>
                  {formatMessage(messages[column as keyof typeof messages])}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(({ id, name, s3DownloadUri, dateCreated }) => (
              <tr className="table-row" key={id}>
                <td>{name}</td>
                <td>{dateCreated}</td>
                <ActionsColumn
                  id={id}
                  formatMessage={formatMessage}
                  s3DownloadUri={s3DownloadUri}
                />
              </tr>
            ))}
            {!data ||
              (data.length === 0 && (
                <tr key="no-data">
                  <td colSpan={columns.length}>
                    {formatMessage(messages.noData)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {!loader && (
        <TablePagination
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onPageChange={(event, nextPage) => {
            setPage(nextPage)
          }}
          onRowsPerPageChange={(
            event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            const { value } = event.target
            setPage(0)
            setRowsPerPage(Number(value))
          }}
          component="div"
          className="table-pagination"
          labelRowsPerPage={formatMessage({ id: 'rows_per_page' })}
          backIconButtonProps={{
            'aria-label': formatMessage({ id: 'previous_page' }),
          }}
          nextIconButtonProps={{
            'aria-label': formatMessage({ id: 'next_page' }),
          }}
        />
      )}
      <CircleLoader loading={loader} />
    </Table>
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) =>
      | Record<string, boolean>
      | Psfs
      | Map<string, { data: List<PdfReport>; count: number }>
      | boolean
  }) => ({
    loader: state.getIn(['pdfReports', 'isLoading']) as boolean,
    tablePsfs: state.getIn(['pdfReports', 'tablePsfs']) as Psfs,
    tableData: state.getIn(['pdfReports', 'tableData']) as Map<
      string,
      { data: List<PdfReport>; count: number }
    >,
  }),
  {},
)(injectIntl(PdfReportsTable))
